import React, { useEffect, useState } from 'react';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import { useCampuses } from '../../contexts/CampusContext';
import { useEvents } from '../../contexts/EventsContext';
import { useLeaders } from '../../contexts/LeaderContext';
import { useMinistries } from '../../contexts/MinistryContext';
import MinistryLayout from './MinistryLayout';

const WomenMinistry = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ministryId = queryParams.get('ministryId');
  const { leaders } = useLeaders();
  const { campuses } = useCampuses();
  const { ministries } = useMinistries();
  const [ministryLeaders, setMinistryLeaders] = useState([]);
  const [ministry, setMinistry] = useState({});
  const [ministryEvents, setMinistryEvents] = useState([]);
  const { events } = useEvents();

  useEffect(() => {
    if (leaders && campuses && ministries) {
      const enhancedLeaders = leaders
        .filter(leader => leader.ministryId === ministryId)
        .map(leader => {
          const campus = campuses.find(c => c.id === leader.campusId);
          return {
            ...leader,
            campusName: campus ? campus.name : 'Unknown Campus'
          };
        });
      setMinistryLeaders(enhancedLeaders);
      
      const currentMinistry = ministries.find(m => m.id === ministryId);
      setMinistry(currentMinistry || {});
    }
  }, [leaders, campuses, ministries, ministryId]);
  
  useEffect(() => {
    if (events && ministry.name) {
      const filteredEvents = events.filter(event => {
        if (!event || !event.targetGroup) return false;
        return event.targetGroup.toLowerCase() === ministry.name.toLowerCase();
      });
      
      const sortedEvents = filteredEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
      setMinistryEvents(sortedEvents);
    } else {
      setMinistryEvents([]);
    }
  }, [events, ministry.name]);
  
  return (
    <MinistryLayout
      title="Women's Ministry"
      description="Empowering women to grow in faith, develop godly character, and serve with their unique gifts."
      leaderName="Sarah Johnson"
      leaderTitle="Women's Ministry Director"
      leaders={ministryLeaders}
      coverImage={ministry.cover_image}
      vision="To see women growing in their relationship with God, discovering their identity in Christ, and using their gifts to impact their families, church, and community."
      mission="To create a nurturing environment where women of all ages and backgrounds can connect, grow spiritually, and be equipped to live out their faith in practical ways."
      activities={[
        "Weekly Bible studies focusing on women's unique challenges and roles",
        "Monthly prayer and fellowship gatherings",
        "Annual women's retreat for spiritual refreshment",
        "Mentoring program pairing older and younger women",
        "Community outreach to single mothers and women in need",
        "Special events addressing women's health, family, and career"
      ]}
      schedules={ministryEvents}
    >
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Upcoming Events</h3>
        
        {ministryEvents.length > 0 ? (
          <div className="space-y-4">
            {ministryEvents.map(event => (
              <div key={event.id} className="border-l-4 border-indigo-500 pl-4">
                <h4 className="font-medium text-gray-900">{event.name}</h4>
                <p className="text-gray-600 mb-1">
                  {new Date(event.date).toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })}
                  {event.time && ` • ${event.time}`}
                </p>
                <p className="text-gray-700">{event.description}</p>
                {event.location && (
                  <p className="text-sm text-gray-500 mt-1">
                    <FaMapMarkerAlt className="inline mr-1" />
                    {event.location}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <svg 
              className="mx-auto h-12 w-12 text-blue-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
            <h4 className="mt-2 text-lg font-medium text-gray-900">No Upcoming Events</h4>
            <p className="mt-1 text-sm text-gray-600">
              Check back soon for upcoming {ministry.name || 'Women\'s Ministry'} events!
            </p>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Testimonials</h3>
        <div className="space-y-6">
          <blockquote className="italic text-gray-700 border-l-4 border-gray-200 pl-4">
            "The Women's Ministry has been such a blessing in my life. I've found deep friendships, grown in my faith, and discovered gifts I never knew I had. It's truly a place where women support each other through life's joys and challenges."
            <footer className="mt-2 text-gray-600 font-medium">— Rebecca W.</footer>
          </blockquote>
          <blockquote className="italic text-gray-700 border-l-4 border-gray-200 pl-4">
            "As a new believer, I was nervous about getting involved, but the women here welcomed me with open arms. The Bible studies have helped me understand God's Word and apply it to my life as a wife and mother."
            <footer className="mt-2 text-gray-600 font-medium">— Michelle T.</footer>
          </blockquote>
        </div>
      </div>
    </MinistryLayout>
  );
};

export default WomenMinistry;